import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { fmtInt, type ThroughputPhase, type ThroughputSeries } from '../engine';
import { PHASE_COLORS } from './chartTokens';

const PHASES: ThroughputPhase[] = ['warmup', 'peak', 'tail'];
const FRAME_MS = 130;

/**
 * Throughput por ventana de captura: el panel oscuro del diseño de dashboards.
 * Una barra por bloque de 15 minutos coloreada por fase (arranque · pico ·
 * cola), el pico como línea de techo, y una REPRODUCCIÓN: cuando el panel
 * entra en pantalla, un cursor recorre los bloques uno a uno (130 ms cada
 * uno), la barra en curso brilla y los marcadores de abajo —hora, capturas
 * del bloque, acumulado, premios, fase— avanzan con él. Se puede pausar,
 * volver a lanzar o saltar a un bloque con un clic.
 */
export function ThroughputTimeline({
  series,
  title,
  stamps,
  claimRate,
  capLabel,
  compact,
  className,
}: {
  series: ThroughputSeries[];
  title?: string;
  /** Un sello por día (`YYYY-MM-DD` → texto), p. ej. el clima registrado. */
  stamps?: Record<string, string>;
  /** Premios entregados por captura, para estimar los entregados hasta cada bloque. */
  claimRate?: number;
  /** Rótulo de la línea de techo; por defecto, el pico del día. */
  capLabel?: string;
  compact?: boolean;
  className?: string;
}) {
  const t = useT();
  const [sel, setSel] = useState(0);
  const [frame, setFrame] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const timer = useRef<number | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const autoPlayed = useRef(false);

  const cur = series[Math.min(sel, Math.max(0, series.length - 1))];
  const blocks = cur?.blocks ?? [];
  const n = blocks.length;
  const f = frame == null ? n - 1 : Math.min(n - 1, frame);

  const stop = () => {
    if (timer.current != null) window.clearInterval(timer.current);
    timer.current = null;
    setPlaying(false);
  };
  const play = () => {
    if (!n) return;
    stop();
    setPlaying(true);
    setFrame(0);
    timer.current = window.setInterval(() => {
      setFrame((prev) => {
        const next = (prev ?? 0) + 1;
        if (next >= n) {
          if (timer.current != null) window.clearInterval(timer.current);
          timer.current = null;
          setPlaying(false);
          return n - 1;
        }
        return next;
      });
    }, FRAME_MS);
  };
  const toggle = () => (playing ? stop() : play());
  const select = (i: number) => {
    stop();
    setSel(i);
    setFrame(null);
  };
  const tabLabel = (s: ThroughputSeries) => `${s.name ? `${s.name} · ` : ''}${s.dayLabel ?? dayjs(s.day).format('MMM D')}`;

  // Se reproduce sola la primera vez que el panel entra en pantalla.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || autoPlayed.current || !n) return;
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !autoPlayed.current) {
          autoPlayed.current = true;
          io.disconnect();
          play();
        }
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n]);
  useEffect(() => () => stop(), []);

  if (!cur || !n) {
    return (
      <div className={cn('rounded-[18px] border p-6 text-center text-sm', className)} style={{ background: 'var(--color-dash-night)', borderColor: '#1d1d1d', color: 'var(--color-dash-night-fg-muted)' }}>
        {t('dash.thru.empty')}
      </div>
    );
  }

  const values = blocks.map((b) => b.count);
  const max = Math.max(cur.peak, 1) * 1.08;
  const cum = values.slice(0, f + 1).reduce((a, b) => a + b, 0);
  const rate = claimRate ?? 0;
  const prizes = Math.round(cum * rate);
  const now = blocks[f]!;
  const phaseColor = PHASE_COLORS[now.phase];
  const stamp = stamps?.[cur.day];
  const barH = compact ? 120 : 190;
  const label = (ms: number) => dayjs(ms).format('h:mm A');
  const axis = [label(blocks[0]!.ms), label(blocks[Math.floor((n - 1) / 2)]!.ms), label(blocks[n - 1]!.ms + 15 * 60_000)];
  const notes: Record<ThroughputPhase, string> = {
    warmup: t('dash.thru.warmupNote', { blocks: cur.third, n: fmtInt(cur.warmup) }),
    peak: t('dash.thru.peakNote', { peak: fmtInt(cur.peak), n: cur.peakWindowBlocks }),
    tail: t('dash.thru.tailNote', { n: fmtInt(cur.tail) }),
  };
  const readouts = [
    { label: t('dash.thru.time'), value: label(now.ms), fg: 'var(--color-dash-lime)' },
    { label: t('dash.thru.perBlock'), value: fmtInt(now.count), fg: 'var(--color-dash-lime)' },
    { label: t('dash.thru.cum'), value: fmtInt(cum), fg: 'var(--color-dash-lime)' },
    ...(rate > 0
      ? [
          { label: t('dash.thru.prizes'), value: fmtInt(prizes), fg: '#5fd9a0' },
          { label: t('dash.thru.ofCapture'), value: `${cur.total ? Math.round((prizes / cur.total) * 100) : 0}%`, fg: '#5fd9a0' },
        ]
      : []),
    { label: t('dash.thru.phaseLabel'), value: t(`dash.phase.${now.phase}`), fg: phaseColor },
  ];

  return (
    <div ref={rootRef} className={cn('flex flex-col gap-[18px] rounded-[18px] border p-[22px]', className)} style={{ background: 'var(--color-dash-night)', borderColor: '#1d1d1d' }}>
      <div className="flex flex-wrap items-center justify-between gap-3.5">
        <div className="flex min-w-0 flex-wrap items-center gap-3.5">
          <span className="dash-mono text-sm font-bold uppercase tracking-[0.14em] text-white">{title ?? t('dash.thru.title')}</span>
          <button
            type="button"
            onClick={toggle}
            className="dash-mono flex items-center gap-2 rounded-[10px] px-5 py-[11px] text-sm font-bold uppercase tracking-[0.12em] transition-colors hover:brightness-95"
            style={{ background: 'var(--color-dash-lime)', color: 'var(--color-dash-ink)', boxShadow: '0 0 22px rgba(200,229,48,0.35)' }}
          >
            {playing ? `■ ${t('dash.thru.pause')}` : `▶ ${t('dash.thru.play')}`}
          </button>
          <span className="dash-mono rounded-lg border px-[13px] py-2 text-xs font-bold uppercase tracking-[0.1em]" style={{ borderColor: phaseColor, color: phaseColor }}>
            {t(`dash.phase.${now.phase}`)}
          </span>
          {stamp && (
            <span className="dash-mono flex items-center gap-2 rounded-lg px-[13px] py-2 text-xs font-bold uppercase tracking-[0.06em]" style={{ background: 'rgba(255,255,255,0.06)', color: '#dadad4' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <circle cx="12" cy="12" r="4" />
                <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6L7 7M17 17l1.4 1.4M18.4 5.6L17 7M7 17l-1.4 1.4" />
              </svg>
              {stamp}
            </span>
          )}
        </div>
        {series.length > 1 && series.length <= 4 && (
          <div className="flex flex-wrap gap-1.5">
            {series.map((s, i) => (
              <button
                key={`${s.name ?? ''}${s.day}`}
                type="button"
                onClick={() => select(i)}
                className="dash-mono rounded-full border px-3.5 py-[9px] text-xs font-bold tracking-[0.06em]"
                style={i === sel ? { background: 'var(--color-dash-lime)', color: 'var(--color-dash-ink)', borderColor: 'var(--color-dash-lime)' } : { background: 'transparent', color: 'var(--color-dash-night-fg-muted)', borderColor: '#2a2a2a' }}
              >
                {tabLabel(s)}
              </button>
            ))}
          </div>
        )}
        {/* Con muchas series (Insights) una píldora por evento y día no cabe:
            un navegador con anterior / siguiente y la lista desplegada. */}
        {series.length > 4 && (
          <div className="flex items-center gap-1.5">
            <button type="button" aria-label="‹" onClick={() => select((sel - 1 + series.length) % series.length)} className="dash-mono rounded-full border px-3 py-[9px] text-xs font-bold hover:border-[var(--color-dash-lime)]" style={{ borderColor: '#2a2a2a', color: 'var(--color-dash-night-fg-muted)' }}>‹</button>
            <select
              value={sel}
              onChange={(e) => select(Number(e.target.value))}
              className="dash-mono max-w-[320px] rounded-full border px-3.5 py-[9px] text-xs font-bold tracking-[0.06em] focus:outline-none"
              style={{ background: 'var(--color-dash-lime)', color: 'var(--color-dash-ink)', borderColor: 'var(--color-dash-lime)' }}
            >
              {series.map((s, i) => (
                <option key={`${s.name ?? ''}${s.day}`} value={i}>
                  {tabLabel(s)}
                </option>
              ))}
            </select>
            <button type="button" aria-label="›" onClick={() => select((sel + 1) % series.length)} className="dash-mono rounded-full border px-3 py-[9px] text-xs font-bold hover:border-[var(--color-dash-lime)]" style={{ borderColor: '#2a2a2a', color: 'var(--color-dash-night-fg-muted)' }}>›</button>
            <span className="dash-mono text-[10px] tracking-[0.06em]" style={{ color: 'var(--color-dash-night-fg-subtle)' }}>{t('dash.thru.nav', { i: sel + 1, n: series.length })}</span>
          </div>
        )}
      </div>

      <div className="relative pt-4">
        <div className="relative border-t border-dashed" style={{ borderColor: 'rgba(251,191,36,0.55)' }}>
          <span className="dash-mono absolute right-0 top-[-19px] text-[10px] tracking-[0.08em]" style={{ color: '#d9a82a' }}>
            {capLabel ?? t('dash.thru.peakCap', { n: fmtInt(cur.peak) })}
          </span>
        </div>
        <div className="flex items-end gap-0.5" style={{ height: barH + 6 }}>
          {blocks.map((b, i) => {
            const c = PHASE_COLORS[b.phase];
            const on = i === f;
            return (
              <button
                key={b.at}
                type="button"
                title={`${label(b.ms)} · ${t('dash.thru.tooltip', { n: fmtInt(b.count), phase: t(`dash.phase.${b.phase}`) })}${b.live ? ` · ${t('dash.thru.live')}` : ''}`}
                onClick={() => {
                  stop();
                  setFrame(i);
                }}
                className="flex h-full min-w-0 flex-1 items-end"
              >
                <span
                  className="w-full rounded-t-[2px] transition-[height] duration-[250ms]"
                  style={{
                    height: `${Math.max(2, Math.round((b.count / max) * barH))}px`,
                    background: on ? `linear-gradient(180deg,#f2f8ce,${c})` : i > f ? 'rgba(200,229,48,0.12)' : c,
                    boxShadow: on ? '0 0 18px rgba(200,229,48,0.55)' : b.live ? '0 0 0 2px #ffffff inset' : 'none',
                  }}
                />
              </button>
            );
          })}
        </div>
        <div className="dash-mono mt-2.5 flex justify-between text-[11px]" style={{ color: 'var(--color-dash-night-fg-subtle)' }}>
          {axis.map((a, i) => (
            <span key={i}>{a}</span>
          ))}
        </div>
      </div>

      {!compact && (
        <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
          {readouts.map((r) => (
            <div key={r.label} className="rounded-xl border p-4" style={{ borderColor: '#1f1f1f', background: '#101010' }}>
              <div className="dash-mono text-2xl font-bold leading-[1.1]" style={{ color: r.fg }}>{r.value}</div>
              <div className="dash-mono mt-2 text-[10px] uppercase tracking-[0.1em]" style={{ color: 'var(--color-dash-night-fg-subtle)' }}>{r.label}</div>
            </div>
          ))}
        </div>
      )}

      {rate > 0 && (
        <div className="flex flex-col gap-[7px]">
          <div className="h-2.5 overflow-hidden rounded-full" style={{ background: '#171717' }}>
            <div className="h-full rounded-full transition-[width] duration-[250ms]" style={{ width: `${cur.total ? Math.min(100, Math.round((prizes / cur.total) * 100)) : 0}%`, background: 'linear-gradient(90deg,#c8e530,#5fd9a0)' }} />
          </div>
          <div className="dash-mono flex justify-between text-[10px]" style={{ color: 'var(--color-dash-night-fg-subtle)' }}>
            <span>0</span>
            <span style={{ color: '#5fd9a0' }}>{t('dash.thru.deliveredSoFar', { n: fmtInt(prizes) })}</span>
            <span>{t('dash.thru.completions', { n: fmtInt(cur.total) })}</span>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-[18px] border-t pt-1" style={{ borderColor: '#1a1a1a' }}>
        {PHASES.map((ph) => (
          <span key={ph} className="dash-mono flex items-center gap-2 text-[11px]" style={{ color: 'var(--color-dash-night-fg-muted)' }}>
            <span className="h-2.5 w-2.5 rounded-[2px]" style={{ background: PHASE_COLORS[ph] }} />
            {t(`dash.phase.${ph}`)}
          </span>
        ))}
        <span className="dash-mono flex items-center gap-2 text-[11px]" style={{ color: 'var(--color-dash-night-fg-muted)' }}>
          <span className="h-0 w-4 border-t border-dashed" style={{ borderColor: 'rgba(251,191,36,0.8)' }} />
          {t('dash.thru.peakLegend')}
        </span>
      </div>

      <p className="text-[13px] leading-[1.55] [text-wrap:pretty]" style={{ color: 'var(--color-dash-night-fg-muted)' }}>{notes[now.phase]}</p>
    </div>
  );
}
